import { css, useTheme } from '@emotion/react';
import { UploadTwo } from '@icon-park/react';
import cn from 'classnames';
import { type FocusEventHandler, Fragment, memo, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';

import type { Upload } from '@didaskill/domain/uploads/types';
import { useFocus } from '@didaskill/ui/utils';

import { Typography } from '../../general/typography/Typography';
import messages from './FileUpload.messages';
import * as styles from './FileUpload.styles';
import { FileUploadContainer, type FileUploadContainerProps } from './FileUploadContainer';
import { FileUploaded, type FileUploadedProps } from './file-uploaded/FileUploaded';
import { FileUploading } from './file-uploading/FileUploading';
import { FileUploadRules } from './rules/FileUploadRules';
import type { FileWithId } from './types';

export type FileUploadProps = Omit<FileUploadContainerProps, 'children'> & {
  /** The dropzone has an error. */
  hasError?: boolean;
  /** The dropzone has a warning. */
  hasWarning?: boolean;
  /** Callback triggered when the dropzone is focused. */
  onFocus?: FocusEventHandler<HTMLDivElement>;
  /** Callback triggered when the dropzone is blurred. */
  onBlur?: FocusEventHandler<HTMLDivElement>;
  onDownload?: (file: Upload) => void;
};

export const FileUploadBase = ({
  hasError = false,
  hasWarning = false,
  onFocus: onFocusProps = undefined,
  onBlur: onBlurProps = undefined,
  onDownload,
  ...props
}: FileUploadProps) => {
  const theme = useTheme();

  const { isFocused, onFocus, onBlur } = useFocus<HTMLDivElement>({
    onFocus: onFocusProps,
    onBlur: onBlurProps,
  });

  // Not destructuring in function params because they all need to be passed to FileUploadContainer.
  const { value, onChange, maxFiles } = props;

  // Remove the file.
  // If multiple files, remove from the value array.
  // If 1 file, set value to null.
  const onRemoveFile: FileUploadedProps['onRemove'] = useCallback(
    (fileId) =>
      onChange(
        maxFiles > 1
          ? ((value as { id: string }[]).filter(
              (file: FileWithId | Upload) => file.id !== fileId,
            ) as FileWithId[] | Upload[])
          : null,
      ),
    [value, onChange, maxFiles],
  );

  const onDownloadFile = useCallback(
    (file: Upload) => {
      if (onDownload) {
        onDownload(file);
      }
    },
    [onDownload],
  );

  return (
    <FileUploadContainer {...props}>
      {({
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject,
        isDropzoneDisabled,
        filesUploading,
        arrayValue,
      }) => (
        <Fragment>
          <div
            {...getRootProps({ onFocus, onBlur })}
            className={cn({
              isFocused,
              isDropzoneDisabled,
              isDragActive,
              isDragAccept,
              isDragReject,
              hasError,
              hasWarning: !hasError && !!hasWarning,
            })}
            css={styles.dropzone}
          >
            <input {...getInputProps()} />

            <UploadTwo
              size={30}
              fill={styles.getIconFills(
                theme,
                isDropzoneDisabled,
                isFocused,
                isDragReject,
                isDragActive,
                hasError,
                hasWarning,
              )}
            />

            <Typography
              variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR}
              css={css`
                margin-top: 15px;
              `}
            >
              <FormattedMessage {...messages.PLACEHOLDER} />
            </Typography>

            <FileUploadRules
              maxFiles={props.maxFiles}
              maxSize={props.maxSize}
              accept={props.accept}
            >
              {({ extensionsRule, maxSizeRule, maxFilesRule }) =>
                !!(extensionsRule || maxSizeRule || maxFilesRule) && (
                  <Typography
                    variant={Typography.VARIANTS.BODY_SMALL_ITALIC}
                    css={css`
                      text-align: center;
                      margin-top: 10px;
                    `}
                  >
                    <div>{extensionsRule}</div>
                    <div>{maxSizeRule}</div>
                    <div>{maxFilesRule}</div>
                  </Typography>
                )
              }
            </FileUploadRules>
          </div>

          {!!(arrayValue.length || filesUploading.length) && (
            <ul css={styles.assetList}>
              {arrayValue.map((file: FileWithId | Upload) => (
                <li key={file.id}>
                  <FileUploaded
                    file={file}
                    onRemove={onRemoveFile}
                    useFileAsValue={props.useFileAsValue}
                    disabled={props.disabled}
                    onDownload={onDownload ? () => onDownloadFile(file as Upload) : undefined}
                  />
                </li>
              ))}

              {filesUploading.map((fileUploading) => (
                <li key={fileUploading.id}>
                  <FileUploading fileUploading={fileUploading} />
                </li>
              ))}
            </ul>
          )}
        </Fragment>
      )}
    </FileUploadContainer>
  );
};

FileUploadBase.displayName = 'FileUpload';

export const FileUpload = memo(FileUploadBase);
